// src/components/Modals/personalinfos/attendanceIssue.jsx

import { useState } from "react";
import axios from "axios";
import toastr from "../../../utils/toast";



function formatModalDate(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

/* ─────────────────────────────────────────────
   ISSUE CONFIG — label, colour, placeholder
───────────────────────────────────────────── */
const ISSUE_CONFIG = {
  no_login: {
    label:       "No login",
    color:       "#dc2626",
    placeholder: "e.g. VPN was down, was on client call, internet outage...",
  },
  late_login: {
    label:       "Late login",
    color:       "#d97706",
    placeholder: "e.g. Doctor appointment, traffic, power cut...",
  },
  no_logout: {
    label:       "No logout recorded",
    color:       "#ea580c",
    placeholder: "e.g. System crash, forgot to logout, left abruptly due to emergency...",
  },
  early_logout: {
    label:       "Left early",
    color:       "#ea580c",
    placeholder: "e.g. Emergency, client site visit, health issue...",
  },
};

/* ─────────────────────────────────────────────
   ATTENDANCE ISSUE MODAL
   Props:
     day        – { date, status, segments, ... }
     issueType  – "no_login" | "late_login" | "no_logout" | "early_logout"
     onClose    – fn()
     onSaved    – fn(date)
───────────────────────────────────────────── */
const AttendanceIssueModal = ({ day, issueType, onClose, onSaved }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token        = localStorage.getItem("token");

  const [comment,   setComment]   = useState("");
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved,     setSaved]     = useState(false);

  const config = ISSUE_CONFIG[issueType] ?? ISSUE_CONFIG.no_login;

  // Find first login time if available
  const sorted     = [...(day.segments ?? [])].sort((a, b) => {
    const toMin = (t) => { if (!t) return 9999; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
    return toMin(a.time) - toMin(b.time);
  });
  const firstLogin = sorted[0]?.time ?? null;
  const lastLogout = sorted.reduce((latest, s) => {
    if (!s.logoutTime) return latest;
    return (!latest || s.logoutTime > latest) ? s.logoutTime : latest;
  }, null);

  function fmtTime(t) {
    if (!t) return null;
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
  }

  // const handleSend = async () => {
  //   if (!comment.trim()) return;
  //   try {
  //     setSaving(true);
  //     setSaveError(null);
  //     await axios.post(
  //       `${API_BASE_URL}/employeeinfos/employee/attendance-issue`,
  //       { date: day.date, comment: comment.trim(), issueType },
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     setSaved(true);
  //     if (onSaved) onSaved(day.date);
  //     setTimeout(() => onClose(), 1200);
  //   } catch (err) {
  //     console.error("Failed to save attendance issue:", err);
  //     setSaveError("Could not send. Please try again.");
  //   } finally {
  //     setSaving(false);
  //   }
  // };

const handleSend = async () => {
  toastr.warning("Feature under construction")
}

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div style={{
        background: "#fff", borderRadius: 16,
        width: "100%", maxWidth: 480,
        padding: "28px 28px 24px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        position: "relative",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      }}>

        {/* ✕ close */}
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 16,
          width: 28, height: 28, borderRadius: "50%",
          border: "none", background: "#f3f4f6",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color: "#6b7280",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Date */}
        <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: "#111827", paddingRight: 32 }}>
          {formatModalDate(day.date)}
        </h2>

        {/* Status line */}
        <div style={{ marginBottom: 18, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, color: "#6b7280" }}>
            Status:{" "}
            <span style={{ fontWeight: 650, color: config.color }}>{config.label}</span>
          </span>

          {/* Show login → logout times if available */}
          {firstLogin && (
            <span style={{
              fontSize: 12, color: "#6b7280",
              background: "#f9fafb", border: "1px solid #e5e7eb",
              borderRadius: 6, padding: "2px 8px",
            }}>
              {fmtTime(firstLogin)}
              {lastLogout
                ? <> → {fmtTime(lastLogout)}</>
                : <span style={{ color: "#dc2626", marginLeft: 6, fontWeight: 600 }}>no logout</span>
              }
            </span>
          )}
        </div>

        {/* Textarea */}
        <label style={{ display: "block", marginBottom: 8, fontSize: 14, fontWeight: 600, color: "#374151" }}>
          Explain what happened{" "}
          <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={config.placeholder}
          rows={4}
          disabled={saving || saved}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "12px 14px", borderRadius: 10,
            border: `1.5px solid ${saveError ? "#fca5a5" : "#0d9488"}`,
            fontSize: 14, color: "#111827",
            resize: "vertical", outline: "none",
            fontFamily: "inherit", background: saving || saved ? "#f9fafb" : "#fff",
            lineHeight: 1.5,
          }}
        />

        {saveError && (
          <p style={{ margin: "6px 0 0", fontSize: 12, color: "#dc2626" }}>{saveError}</p>
        )}
        {saved && (
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#16a34a", fontWeight: 600 }}>
            ✓ Sent to HR successfully
          </p>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={saving} style={{
            padding: "9px 20px", borderRadius: 8,
            border: "1px solid #e5e7eb", background: "#fff",
            fontSize: 14, fontWeight: 600, color: "#374151",
            cursor: saving ? "not-allowed" : "pointer",
          }}>
            Close
          </button>
          <button onClick={handleSend} disabled={saving || saved || !comment.trim()} style={{
            padding: "9px 22px", borderRadius: 8, border: "none",
            background: saving || saved || !comment.trim() ? "#99f6e4" : "#0d9488",
            fontSize: 14, fontWeight: 700, color: "#fff",
            cursor: saving || saved || !comment.trim() ? "not-allowed" : "pointer",
            transition: "background 0.15s", minWidth: 110,
          }}>
            {saving ? "Sending…" : saved ? "Sent ✓" : "Send to HR"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceIssueModal;